import { RawDraftContentBlock, RawDraftContentState } from 'draft-js';
import Linkify from 'react-linkify';

interface ParseMessageObj {
  mentionStrings: string[] | undefined;
  urlStrings: string[] | undefined;
  parts: string[];
}

interface ParseNoteMessageObj {
  mentionStrings: string[] | undefined;
  parts: string[];
}

export function parseNoteMessage(message: string): ParseNoteMessageObj {
  const mentions = message?.match(/\[(.*?)\]/g);

  const mentionStrings = mentions?.map((item) =>
    item.toString().replace('[', '').replace(']', '')
  );
  const parts = message?.split(/\[(.*?)\]/g);

  return {
    mentionStrings,
    parts,
  };
}

export function getParsedMessage(message: string) {
  const { parts, mentionStrings } = parseNoteMessage(message);

  let foundTeamName = false;
  // Message can contain something like the following:
  // BirdsOfPray (@[11ecb105-df77-8c0b-9917-0afdc3ec6ec1:Kyle Flipthree] @[11ecf17f-8041-61ec-9917-0afdc3ec6ec1:Mauricio Gonzalez])
  // When '(@[' is contained in the message, then a team has been found.
  const messageHasTeamName = message.match(/\(@\[/gm) !== null;
  return parts?.map((part: string, idx: number) => {
    const key = `${idx}-${parts.length}`;

    if (mentionStrings?.includes(part)) {
      const matchParts = part.split(':');
      if (matchParts.length > 1) {
        return (
          <span key={key} className="text-danger font-weight-bold">
            @{matchParts[1]}
          </span>
        );
      }
      return part;
    }

    if (messageHasTeamName && !foundTeamName && part.includes('(@')) {
      let teamName = '';
      let wordsBeforeTeamName = [''];
      //Find words that match before ' (@' in part
      const matches = part.match(/^((?!\(@).)*/gm);

      if (matches && matches?.length > 0) {
        const words = matches?.[0].split(' ');
        if (words.length > 1) {
          teamName = words[words.length - 2] ?? null;
          wordsBeforeTeamName = words.splice(0, words.length - 2);

          if (wordsBeforeTeamName.length > 0) {
            wordsBeforeTeamName.push(' ');
          }
          foundTeamName = true;
        }
      }

      if (teamName) {
        return (
          <span key={key}>
            {wordsBeforeTeamName?.join(' ')}
            <span className="text-danger font-weight-bold">{teamName}</span>
            <span className="text-danger font-weight-bold"> (</span>
          </span>
        );
      }
    }

    if (foundTeamName && part.includes(')')) {
      //Matches on @ and removes it if it contained at the end of sentence to prevent issue when another @[user] is used after it
      let wordsAfterTeamName = part.split(')')[1].replace(/@$/, '');

      return (
        <span key={key}>
          <span className="text-danger font-weight-bold">)</span>
          <span>{wordsAfterTeamName}</span>
        </span>
      );
    }

    if (part?.includes('/n')) {
      return part.split('/n').map((line: any) => (
        <span key={key}>
          <Linkify>{`${line.replace('@', '')}`}</Linkify>
          <br />
        </span>
      ));
    }

    return <Linkify key={key}>{`${part.replace('@', '')}`}</Linkify>;
  });
}

export function parseMessage(message: string): ParseMessageObj {
  const mentions = message.match(/\[(.*?)\]/g);

  const mentionStrings = mentions?.map((item) =>
    item.toString().replace('[', '').replace(']', '')
  );

  const topLevelParts = message.split(/\[(.*?)\]/g);

  const urlRegex =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)?/gi;
  const urlStrings = message.match(urlRegex)?.map((item) => item.toString());

  const parts: string[] = [];
  topLevelParts.forEach((topLevelPart) => {
    const childParts = topLevelPart.split(' ');
    childParts.forEach((childPart) => {
      if (childPart) {
        parts.push(childPart);
      }
    });
  });

  return {
    mentionStrings,
    urlStrings,
    parts,
  };
}

export function convertStringToRawState(text: string): RawDraftContentState {
  const lines = text.split('/n');
  let entityKey = 0;
  const entityMap: any = {};

  const blockArray = lines.map((lineText: string): RawDraftContentBlock => {
    const { mentionStrings, parts } = parseNoteMessage(lineText);
    let text = '';
    const entityRanges: any = [];

    parts.forEach((part: string) => {
      entityKey++;
      if (mentionStrings?.includes(part)) {
        const matchParts = part.split(':');
        const name = matchParts[1];

        if (matchParts.length > 1) {
          const mentionStringWithAt = `@${name}`;
          text += mentionStringWithAt;

          return;
        }

        text += part;
      }

      text += part.replace('@', '');
    });

    if (mentionStrings) {
      const mentionsUsedMap: any = {};

      mentionStrings.forEach((part: string, idx: number) => {
        const matchParts = part.split(':');
        const id = matchParts[0];
        const name = matchParts[1];

        /*
          This keeps track of which mention we need to find in the
          case that there are multiple mentions for the same user
        */
        if (!mentionsUsedMap[id]) {
          mentionsUsedMap[id] = 1;
        } else {
          mentionsUsedMap[id]++;
        }

        const mentionStringWithAt = `@${name}`;
        const offset = getOffset(
          text,
          mentionStringWithAt,
          mentionsUsedMap[id]
        );

        const entity = {
          type: 'mention',
          mutability: 'SEGMENTED',
          data: { mention: { name, id } },
        };

        const entityRange = {
          key: idx,
          offset,
          length: mentionStringWithAt.length,
        };

        entityMap[idx] = entity;
        entityRanges.push(entityRange);
      });
    }

    return {
      inlineStyleRanges: [],
      entityRanges,
      depth: 0,
      type: 'unstyled',
      text,
      key: entityKey.toString(),
    };
  });

  return {
    blocks: blockArray,
    entityMap,
  };
}

function getOffset(string: string, subString: string, idx: number) {
  return string.split(subString, idx).join(subString).length;
}

export function addMiddleElipse(str: string) {
  if (str.length > 35) {
    return str.substr(0, 20) + '...' + str.substr(str.length - 10, str.length);
  }
  return str;
}
